<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import countries from 'shared/constants/countries';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import { contactDisplayName } from './contactDisplayName';

// Read-only contact card for the audience contacts list. Everything it shows
// comes in the list response, so a page of contacts costs no extra requests
// (the regular ContactsCard fetches labels, conversations and deals per card).
const props = defineProps({
  contact: { type: Object, required: true },
  labelColors: { type: Object, default: () => ({}) },
});

defineEmits(['open']);

const { t } = useI18n();

const displayName = computed(() => contactDisplayName(props.contact, t));
const companyName = computed(() => props.contact.company?.name || '');

const location = computed(() => {
  const attributes = props.contact.additionalAttributes || {};
  const code = attributes.countryCode || attributes.country_code;
  const country = countries.find(
    item => item.id === (attributes.country || code)
  );
  if (!country) return null;

  return {
    countryCode: country.id,
    text: [attributes.city && `${attributes.city},`, country.name]
      .filter(Boolean)
      .join(' '),
  };
});

const details = computed(() =>
  [props.contact.email, props.contact.phoneNumber].filter(Boolean)
);
</script>

<template>
  <CardLayout layout="row">
    <div class="flex items-center justify-start flex-1 min-w-0 gap-4">
      <Avatar
        :name="displayName"
        :src="contact.thumbnail"
        :size="48"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col flex-1 min-w-0 gap-0.5">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
          <button
            type="button"
            class="p-0 text-base font-medium truncate text-start text-n-slate-12 hover:underline"
            @click="$emit('open', contact.id)"
          >
            {{ displayName }}
          </button>
          <span v-if="companyName" class="inline-flex items-center gap-1.5">
            <span
              class="i-ph-building-light size-4 text-n-slate-10 mb-0.5 shrink-0"
            />
            <span class="text-sm truncate text-n-slate-11">
              {{ companyName }}
            </span>
          </span>
        </div>
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <template v-for="(detail, index) in details" :key="detail">
            <span v-if="index" class="w-px h-3 bg-n-slate-6" />
            <span class="text-sm truncate max-w-72 text-n-slate-11">
              {{ detail }}
            </span>
          </template>
          <template v-if="location">
            <span v-if="details.length" class="w-px h-3 bg-n-slate-6" />
            <span
              class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
            >
              <Flag :country="location.countryCode" class="size-3.5" />
              {{ location.text }}
            </span>
          </template>
          <template v-if="contact.labels?.length">
            <span
              v-if="details.length || location"
              class="w-px h-3 bg-n-slate-6"
            />
            <span
              v-for="label in contact.labels"
              :key="label"
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium text-white"
              :style="{ backgroundColor: labelColors[label] || '#6B7280' }"
            >
              {{ label }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
